/**
 * combatEffects.js — Combat Effect Executors (6 types)
 *
 * Handles armor grants, advantage toggling, bonus dice, and
 * deferred roll triggers (spell attack, trait roll, weapon roll).
 *
 * Deferred effects return deferredAction — the ActionExecutor stops
 * processing remaining effects and the caller hands off to DiceRoller.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #175)
 */

import { resolveExpression } from '../expressions';

// ─── Combat Effect Executors ────────────────────────────────────────────────

export const COMBAT_EFFECT_EXECUTORS = {
  /**
   * Grant temporary armor to the character.
   * Matches existing temporaryArmor shape used by StickyVitalsBar.
   * effect: { type: 'grant_armor', amount?, source?, expiresOn? }
   */
  grant_armor: (effect, context) => {
    const amount = resolveExpression(effect.amount || 1, context);
    context.character.temporaryArmor = {
      amount,
      source: effect.source || context.cardKey,
      expiresOn: effect.expiresOn || 'rest',
    };
    return { characterMutated: true };
  },

  /**
   * Enable advantage for the next roll.
   * Returns sessionStateUpdate — caller merges with setPendingSpellAttack
   * if a deferred spell attack is also in the action.
   * effect: { type: 'set_advantage', message? }
   */
  set_advantage: (effect) => {
    return {
      characterMutated: false,
      sessionStateUpdates: {
        advantageEnabled: true,
        advantageMessage: effect.message || 'Advantage enabled!',
      },
    };
  },

  /**
   * Add bonus dice to the next roll.
   * effect: { type: 'add_bonus_dice', count?, size? }
   */
  add_bonus_dice: (effect) => {
    return {
      characterMutated: false,
      sessionStateUpdates: {
        bonusDice: {
          count: effect.count || 1,
          size: effect.size || 6,
        },
      },
    };
  },

  /**
   * DEFERRED: Trigger a spell attack via DiceRoller.
   * Stops effect chain — DiceRoller takes over.
   * effect: { type: 'trigger_spell_attack', spellName?, spellcastTrait?, damage?, damageType? }
   */
  trigger_spell_attack: (effect, context) => {
    return {
      characterMutated: false,
      deferredAction: {
        type: 'spell_attack',
        payload: {
          spellName: effect.spellName || context.mechanic?.meta?.name || context.cardKey,
          cardKey: context.cardKey,
          spellcastTrait: effect.spellcastTrait || null,
          damage: effect.damage || null,
          damageType: effect.damageType || 'magic',
        },
      },
    };
  },

  /**
   * DEFERRED: Trigger a trait roll via DiceRoller.
   * effect: { type: 'trigger_trait_roll', trait, difficulty?, featureName?, successMessage?, failureMessage? }
   */
  trigger_trait_roll: (effect, context) => {
    return {
      characterMutated: false,
      deferredAction: {
        type: 'trait_roll',
        payload: {
          rollType: 'trait',
          trait: effect.trait,
          difficulty: effect.difficulty || null,
          featureName: effect.featureName || context.cardKey,
          successMessage: effect.successMessage || null,
          failureMessage: effect.failureMessage || null,
        },
      },
    };
  },

  /**
   * DEFERRED: Trigger a weapon roll via DiceRoller.
   * effect: { type: 'trigger_weapon_roll', attackTrait?, weaponId?, icon? }
   */
  trigger_weapon_roll: (effect) => {
    return {
      characterMutated: false,
      deferredAction: {
        type: 'weapon_roll',
        payload: {
          rollType: 'weapon',
          attackTrait: effect.attackTrait || 'Finesse',
          weaponId: effect.weaponId || null,
          icon: effect.icon || null,
        },
      },
    };
  },
};
